// job_poll — wait for a generic async MW job by polling the job-status
// endpoint, mirroring the proven NarrationJobTracker / tts_job_poll loop.
//
// A background endpoint returns 202 { jobId, room } and the real payload is
// read from the job result once the worker reports status:"completed". The
// job-status endpoint (GET api/jobs/{id}) lives on MW_API_URL. Response shape:
// { status, progress, result, error }. Unlike tts_job_poll (which extracts an
// audioUrl), this returns the raw `result` object as-is — use it for jobs whose
// result is an arbitrary payload (e.g. infographic-images-generate → mapped
// scene).

import { authedFetch } from "./http_service";

const POLL_INTERVAL = 3000;

// Job-status endpoint base: MW_API_URL with a guaranteed trailing slash.
function resolveJobsBase() {
  const b = process.env.MW_API_URL || "";
  return b.endsWith("/") ? b : `${b}/`;
}

/**
 * Poll GET {MW_API_URL}api/jobs/{jobId} until the job completes, then resolve
 * with the raw job result payload.
 *
 * @param {string} jobId  the jobId returned by the 202 submit response
 * @param {{ timeoutMs?: number }} [opts]  give up after this long (default 10 min)
 * @returns {Promise<any>}  the completed job's `result`.
 *   Rejects with Error("no-job" | "timeout" | "no-result" | <backend error>).
 */
export function waitForJob(jobId, { timeoutMs = 600000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!jobId) {
      reject(new Error("no-job"));
      return;
    }
    const url = `${resolveJobsBase()}api/jobs/${jobId}`;
    const maxAttempts = Math.max(1, Math.ceil(timeoutMs / POLL_INTERVAL));
    let attempts = 0;
    let timer = null;
    let settled = false;

    const settle = (fn, arg) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      fn(arg);
    };

    const tick = async () => {
      attempts += 1;
      if (attempts > maxAttempts) {
        settle(reject, new Error("timeout"));
        return;
      }
      try {
        const res = await authedFetch(url, {
          headers: { Accept: "application/json" },
        });
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          if (data.status === "completed") {
            if (data.result == null) {
              settle(reject, new Error("no-result"));
              return;
            }
            settle(resolve, data.result);
            return;
          }
          if (data.status === "failed") {
            settle(reject, new Error(data.error || "failed"));
            return;
          }
          // pending / processing → keep polling
        }
        // non-OK response (e.g. job not registered yet) → keep polling
      } catch {
        // network blip — keep polling
      }
      timer = setTimeout(tick, POLL_INTERVAL);
    };

    tick();
  });
}
